import { Injectable } from "@angular/core";
import { Fiat, FiatWallet, Wallet } from "@models";
import { FiatService } from "./currency";
import { FiatWalletService } from "./wallet/fiat_wallet.service";
import { AccountService } from "../../account/services/account.service";
import { TradableWalletListService } from "./wallet/tradable_wallet_list.service";
import { UserService } from "./user.service";
import { TradableListService } from "./currency/tradable_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Index } from "@models/asset/index/index.model";
import { convertTradableToFiat, toCurrencyByFiatId, toEuroByFiatId } from "@helpers/tradable-convert";
import { TranslationService } from "@core-services/translation.service";

export interface TooltipCoinOptions {
	action: TooltipTradableAction;
	tradable?: Tradable;
	fiat: Fiat;
	checkAllTradableWallets?: boolean;
	tradableWallet?: TradableWallet;
	details?: string;
	isStaking?: boolean;
}

export enum TooltipTradableAction {
	BUY = "buy",
	SELL = "sell",
	SWAP = "swap",
	SEND = "send",
	SAVINGS = "savings",
	DEPOSIT = "deposit",
	WITHDRAWAL = "withdrawal",
	TRANSFER = "transfer",
	LIMIT_ORDER = "limit_order",
	LEVERAGE = "leverage"
}

@Injectable({
	providedIn: "root"
})
export class TooltipService {
	public constructor(
		private fiatService: FiatService,
		private fiatWalletService: FiatWalletService,
		private readonly walletService: TradableWalletListService,
		private tradableListService: TradableListService,
		private translationService: TranslationService,
		private accountService: AccountService,
		private userService: UserService
	) {}

	public getAmountForTooltip(options: TooltipCoinOptions): string {
		return this.getAmountForCoinsTooltip(options);
	}

	public getTooltip(o: TooltipCoinOptions): string {
		// AML5
		if (this.userService.isVerificationMandatory) {
			switch (o.action) {
				case TooltipTradableAction.BUY:
					return "action_buy_unverified_tooltip";
				case TooltipTradableAction.SELL:
					return "action_sell_unverified_tooltip";
				case TooltipTradableAction.SWAP:
					return "action_swap_unverified_tooltip";
				case TooltipTradableAction.SEND:
					return "action_send_unverified_tooltip";
				case TooltipTradableAction.SAVINGS:
					return "action_savings_plan_unverified_tooltip";
				case TooltipTradableAction.DEPOSIT:
					return "action_deposit_unverified_tooltip";
				case TooltipTradableAction.WITHDRAWAL:
					return "action_withdraw_unverified_tooltip";
				default:
					return "";
			}
		}

		if (o.tradable) {
			// Not available
			if (
				!o.tradable.isAvailable() &&
				(o.action === TooltipTradableAction.BUY ||
					o.action === TooltipTradableAction.SELL ||
					o.action === TooltipTradableAction.SWAP)
			) {
				return this.translationService.translate("unavailable_under_maintenance");
			}

			// Maintenance
			if (
				o.tradable.isMaintenance() &&
				(o.action === TooltipTradableAction.SEND || o.action === TooltipTradableAction.DEPOSIT)
			) {
				return this.translationService.translate("unavailable_under_maintenance");
			}

			// Buy
			if (o.action === TooltipTradableAction.BUY) {
				if (!o.tradable.isBuyActive()) {
					return this.translationService.translate("coin_buy_not_active_for_action_buy", [
						o.tradable.getSymbol(false, true)
					]);
				}
			}

			// Sell
			if (o.action === TooltipTradableAction.SELL) {
				if (!o.tradable.isSellActive()) {
					return "action_sell_inactive_tooltip";
				}

				// Check if at least 1 wallet can be used for sell
				if (o.checkAllTradableWallets) {
					const cryptoWallets = this.getCryptoWalletsAboveMinSell(o.tradable, o.fiat);
					if (cryptoWallets.length === 0) {
						return "action_sell_tooltip";
					}
				} else {
					// Specific wallet
					const convertedCoinBalance: number = toEuroByFiatId(
						convertTradableToFiat(o.tradable, o.tradableWallet.getBalance(), o.fiat),
						o.fiat
					);
					const walletBalance =
						o.tradable instanceof Index ? o.tradableWallet.getBalance() : convertedCoinBalance; // Index balance is already in fiat EUR and does not need to be converted
					if (walletBalance < o.tradable.getMinSell()) {
						if (o.isStaking) {
							return "unstake_to_sell_alert";
						}
						return "action_sell_tooltip";
					}
				}
			}

			// Swap (currently, using the same min limit as for Sell)
			if (o.action === TooltipTradableAction.SWAP) {
				if (
					this.accountService.account.getSwapDisabled() &&
					this.userService.user.getVerified() === "unverified"
				) {
					return "action_swap_unverified_tooltip";
				}

				if (
					!o.tradable.isBuyActive() ||
					!o.tradable.isSellActive() ||
					this.accountService.account.getSwapDisabled()
				) {
					return this.translationService.translate("coin_buy_not_active_for_action_swap", [
						o.tradable.getSymbol(false, true)
					]);
				}

				if (o.checkAllTradableWallets) {
					const cryptoWallets = this.getCryptoWalletsAboveMinSell(o.tradable, o.fiat);
					if (cryptoWallets.length === 0) {
						return "action_swap_tooltip";
					}
				} else {
					if (o.tradableWallet?.getBalance()) {
						const convertedCoinBalance: number = toEuroByFiatId(
							convertTradableToFiat(o.tradable, o.tradableWallet.getBalance(), o.fiat),
							o.fiat
						);
						const walletBalance =
							o.tradable instanceof Index ? o.tradableWallet.getBalance() : convertedCoinBalance; // Index balance is already in fiat EUR and does not need to be converted
						if (o.details !== "reverse_swap" && walletBalance < o.tradable.getMinSell()) {
							return "action_swap_tooltip";
						}
					}
				}
			}

			// Send
			if (o.action === TooltipTradableAction.SEND) {
				if (!o.tradable.isWithdrawActive()) {
					return "action_send_inactive_tooltip";
				}
				if (o.checkAllTradableWallets) {
					// Does not make sense
					const walletWithBalance = this.getCryptoWalletWithBalance(o.tradable);
					if (!walletWithBalance) {
						return "action_send_tooltip";
					}
				} else {
					if (o.tradableWallet.getBalance() <= 0) {
						return "action_send_tooltip";
					}
				}
			}

			// Transfer
			if (o.action === TooltipTradableAction.TRANSFER) {
				if (this.walletService.getWalletsByTradableId(o.tradable.getId()).length === 1) {
					return this.translationService.translate("action_transfer_not_enough_wallets", [
						o.tradable.getSymbol(false, true)
					]);
				}
				if (o.checkAllTradableWallets && this.walletService.getNonEmptyWallets(o.tradable).length === 0) {
					return "action_transfer_not_enough_funds";
				}
				if (o.tradableWallet && o.tradableWallet.getBalance() === 0) {
					return "action_transfer_no_funds_in_current";
				}
				return "action_send_inactive_tooltip";
			}
		}

		// Deposit
		if (o.action === TooltipTradableAction.DEPOSIT) {
			if (this.userService.isVerificationMandatory) {
				return "action_deposit_unverified_tooltip";
			}
			if (!!o.tradable && !o.tradable.isDepositActive()) {
				return "action_deposit_tooltip";
			}
		}

		// Savings
		if (o.action === TooltipTradableAction.SAVINGS) {
			if (!this.userService.user.isVerified()) {
				return "action_savings_verify_tooltip";
			}

			if (this.accountService.account.getSavingsDisabled()) {
				return "action_savings_disabled_tooltip";
			}
		}

		return "";
	}

	public getAmountForCoinsTooltip(o: TooltipCoinOptions): string {
		if (
			(o.action === TooltipTradableAction.BUY && !o.tradable.isBuyActive()) ||
			(o.action === TooltipTradableAction.SELL && !o.tradable.isSellActive()) ||
			(o.action === TooltipTradableAction.SWAP && !o.tradable.isSellActive()) ||
			(o.action === TooltipTradableAction.SEND &&
				!o.tradable.isWithdrawActive() &&
				!o.tradable.isTransferActive()) ||
			(o.action === TooltipTradableAction.DEPOSIT && !o.tradable.isDepositActive()) ||
			o.action === TooltipTradableAction.TRANSFER
		) {
			return o.tradable.getSymbol(false, true);
		}
		if (o.action === TooltipTradableAction.SEND) {
			return "" + o.tradable.getAssetSettings().getMinWithdrawal();
		}

		let min = 0;
		if (o.action === TooltipTradableAction.BUY) {
			min = toCurrencyByFiatId(o.tradable.getMinBuy(), o.fiat);
		}
		if (o.action === TooltipTradableAction.SELL || o.action === TooltipTradableAction.SWAP) {
			min = toCurrencyByFiatId(o.tradable.getMinSell(), o.fiat);
		}
		return "" + min;
	}

	private getFiatWalletsAboveMinBuy(coin: Tradable): FiatWallet[] {
		return this.fiatWalletService.getNonEmptyWallets().filter((fiatWallet) => {
			const fiatCurrency = this.fiatService.getItemById(fiatWallet.getFiatId());
			const fiatBalance = toEuroByFiatId(fiatWallet.getBalance(), fiatCurrency);
			return fiatBalance >= coin.getMinBuy();
		});
	}

	public getCryptoWalletsAboveMinSell(tradable: Tradable, fiat: Fiat): TradableWallet[] {
		return this.walletService
			.getNonEmptyWallets()
			.filter((tradableWallet) => !this.isUnderMinSell(tradable, tradableWallet, fiat));
	}

	private getCryptoWalletWithBalance(tradable: Tradable): TradableWallet {
		return this.walletService.getWalletsByTradableId(tradable.getId()).find((w) => w.getBalance() > 0);
	}

	public getAvailableSwapWallets(fiat: Fiat): TradableWallet[] {
		return this.walletService.getNonEmptyWallets().filter((wallet) => {
			const tradable: Tradable = this.getTradableById(wallet.getCurrencyId());
			return !this.isDisabled(TooltipTradableAction.SWAP, tradable, fiat, wallet);
		});
	}

	public getTradableById(currencyId: string): Tradable {
		return this.tradableListService.getItemById(currencyId);
	}

	public isUnderMinSell(tradable: Tradable, wallet: Wallet, fiat: Fiat): boolean {
		let walletAmount: number = wallet?.getBalance();
		if (!(tradable instanceof Index)) {
			walletAmount = convertTradableToFiat(tradable, wallet.getBalance(), fiat);
			walletAmount = toEuroByFiatId(walletAmount, fiat);
		}
		return walletAmount < tradable.getMinSell();
	}

	public hasOtherAssetsToSwap(tradable: Tradable, fiat: Fiat): boolean {
		const list = this.walletService
			.getList()
			.getValue()
			.filter((w) => !w.isNew());
		return list.some(
			(wallet) =>
				toEuroByFiatId(
					convertTradableToFiat(
						this.tradableListService.getItemById(wallet.getCurrencyId()),
						wallet.getBalance(),
						fiat
					),
					fiat
				) >= tradable.getMinSell()
		);
	}

	public isDisabled(action: string, tradable: Tradable, fiat: Fiat, wallet: Wallet): boolean {
		if (!tradable) {
			return;
		}

		const walletBalance = wallet.getBalance();
		if (this.userService.isVerificationMandatory) {
			return true;
		}

		if (action === TooltipTradableAction.BUY) {
			if (!tradable.isBuyActive() || !tradable.isAvailable()) {
				return true;
			}
		}
		if (action === TooltipTradableAction.SELL) {
			if (!tradable.isAvailable()) {
				return true;
			}
			if (!tradable.isSellActive()) {
				return true;
			}

			return this.isUnderMinSell(tradable, wallet, fiat);
		}

		if (action === TooltipTradableAction.SWAP) {
			if (!tradable.isBuyActive() && !tradable.isSellActive()) {
				return true;
			}
			if (
				!tradable.isSellActive() &&
				this.walletService
					.getNonEmptyWallets()
					.filter((tradableWallet) => this.checkIfTradableWalletHasMoreThanMinimumToSell(tradableWallet))
					.length <= 1
			) {
				return true;
			}
			if (!tradable.isBuyActive() && this.isUnderMinSell(tradable, wallet, fiat)) {
				return true;
			}
			if (
				wallet instanceof TradableWallet &&
				!wallet.isDefault() &&
				this.isUnderMinSell(tradable, wallet, fiat)
			) {
				return true;
			}
			return this.accountService.account.getSwapDisabled();
		}

		if (action === TooltipTradableAction.SEND) {
			if (walletBalance === 0 || !tradable.isWithdrawActive() || tradable.isMaintenance()) {
				return true;
			}
		}
		if (action === TooltipTradableAction.DEPOSIT) {
			if (!tradable.isDepositActive() || tradable.isMaintenance()) {
				return true;
			}
		}
		if (action === TooltipTradableAction.TRANSFER) {
			return (
				this.walletService.getNonEmptyWallets(tradable).length < 1 ||
				walletBalance === 0 ||
				this.walletService.getWalletsByTradableId(tradable.getId()).length < 2 ||
				tradable.isMaintenance() ||
				!tradable.isTransferActive()
			);
		}
		if (action === TooltipTradableAction.SAVINGS) {
			if (!tradable.isBuyActive() || !tradable.isAvailable()) {
				return true;
			}
		}
		return false;
	}

	private checkIfTradableWalletHasMoreThanMinimumToSell(tradableWallet: TradableWallet): boolean {
		const tradable: Tradable = this.tradableListService.getItemById(tradableWallet.getCurrencyId());
		const defaultFiat = this.accountService.getDefaultFiat();
		let amount = (tradable.getPrice() * tradableWallet.getBalance()) / defaultFiat.toEuroRate();
		if (tradable instanceof Index) {
			amount = tradableWallet.getBalance();
		}
		return +amount.toFixed(defaultFiat.getPrecision()) >= tradable.getMinSell();
	}
}
